import type { MenuItem, SimplifiedNav, SimplifiedNavLink } from './header';
import { stateList } from './state-nav-data';

/**
 * localStorage key holding the user's explicitly-selected DEC state slug
 * (e.g. "california"). Written only on an explicit state-pill/card selection;
 * read on global/default pages so a previously-chosen state "sticks" across
 * navigation (EPS-4220). State pages always win for the nav rules shown while
 * there, and never overwrite this value.
 */
export const DEC_STATE_STORAGE_KEY = 'dec_selected_state';

// Top-level item text (from the existing per-state nav data) that should map to
// each simplified bucket. Everything that doesn't match a named bucket flows
// into the "More" dropdown, which keeps the simplified bar stable across the
// brand's bespoke per-state menus.
const DRIVERS_ED_TOPS = ['Drivers Ed', "Learner's Permit"];
const TRAFFIC_SCHOOL_TOPS = ['Traffic School'];
const TEST_PREP_TOPS = [
  'Permit Test Prep',
  'Practice Permit Test',
  'Knowledge Test Prep',
  'Adult Final Exam Prep',
  "Learner's Permit Test",
];

/** Test Prep is a single national page for every state. */
const NATIONAL_TEST_PREP_HREF = '/permit-test-prep-courses/';

/** Flatten a menu group (top item + sub-items) into renderable links, dropping link-less parents (e.g. "Community"). */
function groupToLinks(group: MenuItem[]): SimplifiedNavLink[] {
  const links: SimplifiedNavLink[] = [];
  for (const item of group) {
    if (item.href) {
      links.push({ text: item.text, href: item.href });
    }
  }
  return links;
}

/**
 * Re-present the existing, fully state-resolved menu groups in the simplified
 * CRO-1093 layout. This is a presentation transform — it does not change labels
 * or per-state visibility produced by the existing data pipeline, and passes
 * hrefs through unchanged except Test Prep, which always resolves to the
 * national page. Unmatched groups (bespoke per-state items) fold into "More".
 */
export function buildSimplifiedNav(menuGroups: MenuItem[][] | null | undefined): SimplifiedNav {
  const nav: SimplifiedNav = {
    driversEd: { label: 'Drivers Ed', href: null, items: [] },
    trafficSchool: { label: 'Traffic School', href: null, items: [] },
    testPrep: { label: 'Test Prep', href: null, items: [] },
    more: { label: 'More', href: null, items: [] },
  };

  if (!menuGroups) {
    return nav;
  }

  for (const group of menuGroups) {
    if (!group || !group[0]) {
      continue;
    }
    const topText = group[0].text;

    if (DRIVERS_ED_TOPS.includes(topText) && nav.driversEd.items.length === 0) {
      nav.driversEd.items = groupToLinks(group);
    } else if (TRAFFIC_SCHOOL_TOPS.includes(topText) && !nav.trafficSchool.href) {
      nav.trafficSchool.href = group[0].href;
    } else if (TEST_PREP_TOPS.includes(topText) && !nav.testPrep.href) {
      nav.testPrep.href = NATIONAL_TEST_PREP_HREF;
    } else {
      nav.more.items.push(...groupToLinks(group));
    }
  }

  return nav;
}

/** Resolve a state slug (e.g. "california") to its display name, or null when unknown. */
export function displayNameForSlug(slug: string | null | undefined): string | null {
  if (!slug) {
    return null;
  }
  const match = stateList.find((state) => state.href === `/${slug}/`);
  return match ? match.text : null;
}

/** Derive the state slug from a state-list href (e.g. "/california/" -> "california"). */
export function slugFromHref(href: string): string {
  return href.replace(/^\/|\/$/g, '');
}
